import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Receipt, TicketCheck, Wallet, Percent, CheckCircle2, XCircle, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useCurrentPartner } from "@/hooks/useCurrentPartner";
import { DateRangePicker, type DateRange } from "@/components/DateRangePicker";
import { DashCard, DashHeading, MetricCard, SectionTitle } from "@/components/dashboard";
import { formatDuration, formatMoney } from "@/lib/format";

type RevenueReport = {
  period: { from: string; to: string };
  currency: string;
  issued_count: number;
  issued_gross_cents: number;
  issued_commission_cents: number;
  issued_ponos_net_cents: number;
  redeemed_count: number;
  redeemed_gross_cents: number;
  redeemed_ponos_net_cents: number;
  voided_count: number;
  voided_gross_cents: number;
  expired_unredeemed_count: number;
  breakdown_by_duration: {
    duration_days: number;
    issued_count: number;
    issued_gross_cents: number;
    issued_ponos_net_cents: number;
    redeemed_count: number;
    redeemed_gross_cents: number;
    redeemed_ponos_net_cents: number;
  }[];
};

function defaultRange(): DateRange {
  const d = new Date();
  const from = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
  const to = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().slice(0, 10);
  return { from, to };
}

export default function ReportsRevenuePage() {
  const { data: partner } = useCurrentPartner();
  const [range, setRange] = useState<DateRange>(defaultRange());

  const { data, isLoading } = useQuery<RevenueReport>({
    queryKey: ["revenue-report", range.from, range.to],
    queryFn: async () =>
      (await api.get<RevenueReport>("/partner/reports/revenue", { params: { from: range.from, to: range.to } })).data,
    enabled: !!partner,
  });

  if (!partner) return <div className="text-sm text-neutral-500">Cargando…</div>;

  return (
    <div>
      <DashHeading
        title="Ingresos"
        description="Facturación y activación real en el período seleccionado. 'Facturable' incluye todo lo emitido; 'Activación real' solo los códigos que un usuario canjeó."
        actions={<DateRangePicker value={range} onChange={setRange} />}
      />

      {/* Comisión partner (siempre visible arriba) */}
      <DashCard title="Comisión partner" className="mb-6">
        {partner.commission ? (
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-emerald-50 p-2 border border-emerald-100 flex-shrink-0">
              <Percent className="h-4 w-4 text-emerald-700" strokeWidth={2} />
            </div>
            <div>
              <div className="flex items-baseline gap-1">
                <span className="text-5xl font-semibold text-neutral-900 tabular-nums leading-none">
                  {Number(partner.commission.commission_pct).toFixed(2)}
                </span>
                <span className="text-2xl text-neutral-400 font-medium">%</span>
              </div>
              <p className="mt-3 text-sm text-neutral-600 leading-relaxed max-w-sm">
                Comisión aplicada sobre el precio bruto de cada código emitido.
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-neutral-500">
            Sin comisión configurada todavía. Contáctanos para activarla.
          </p>
        )}
      </DashCard>

      {/* Facturable */}
      <div className="mb-10">
        <SectionTitle hint={`${range.from} — ${range.to}`}>Facturable</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Códigos emitidos"
            value={data?.issued_count ?? "—"}
            icon={<TicketCheck className="h-4 w-4" strokeWidth={1.75} />}
          />
          <MetricCard
            title="Bruto"
            value={formatMoney(data?.issued_gross_cents ?? 0, partner.currency)}
            icon={<Receipt className="h-4 w-4" strokeWidth={1.75} />}
          />
          <MetricCard
            title="Comisión partner"
            value={formatMoney(data?.issued_commission_cents ?? 0, partner.currency)}
            icon={<Percent className="h-4 w-4" strokeWidth={1.75} />}
          />
          <MetricCard
            title="Neto NiuFit"
            value={formatMoney(data?.issued_ponos_net_cents ?? 0, partner.currency)}
            icon={<Wallet className="h-4 w-4" strokeWidth={1.75} />}
          />
        </div>
      </div>

      {/* Activación real */}
      <div className="mb-10">
        <SectionTitle>Activación real</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            title="Códigos canjeados"
            value={data?.redeemed_count ?? "—"}
            icon={<CheckCircle2 className="h-4 w-4" strokeWidth={1.75} />}
          />
          <MetricCard
            title="Bruto canjeado"
            value={formatMoney(data?.redeemed_gross_cents ?? 0, partner.currency)}
            icon={<Receipt className="h-4 w-4" strokeWidth={1.75} />}
          />
          <MetricCard
            title="Neto NiuFit"
            value={formatMoney(data?.redeemed_ponos_net_cents ?? 0, partner.currency)}
            icon={<Wallet className="h-4 w-4" strokeWidth={1.75} />}
          />
        </div>
      </div>

      {/* Contexto operacional */}
      <div className="mb-10">
        <SectionTitle hint="Info sobre códigos emitidos">Contexto operacional</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            title="Anulados"
            value={data?.voided_count ?? "—"}
            hint="Códigos anulados por el partner. Se restan del bruto facturable."
            icon={<XCircle className="h-4 w-4" strokeWidth={1.75} />}
          />
          <MetricCard
            title="Bruto anulado"
            value={formatMoney(data?.voided_gross_cents ?? 0, partner.currency)}
            hint="Total descontado del facturable por anulaciones."
            icon={<Receipt className="h-4 w-4" strokeWidth={1.75} />}
          />
          <MetricCard
            title="Expiraron sin canjear"
            value={data?.expired_unredeemed_count ?? "—"}
            hint="El cliente no los usó a tiempo. Siguen contando en el bruto facturable — la venta ya se ejecutó."
            icon={<Clock className="h-4 w-4" strokeWidth={1.75} />}
          />
        </div>
      </div>

      {/* Breakdown */}
      <DashCard title="Desglose por duración" bodyClassName="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50/50 border-b border-neutral-200">
              <tr className="text-left text-xs font-medium text-neutral-600">
                <th className="px-6 py-3">Plan</th>
                <th className="px-6 py-3">Emitidos</th>
                <th className="px-6 py-3">Bruto emitido</th>
                <th className="px-6 py-3">Neto emitido</th>
                <th className="px-6 py-3">Canjeados</th>
                <th className="px-6 py-3">Bruto canjeado</th>
                <th className="px-6 py-3">Neto canjeado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {isLoading && (
                <tr><td colSpan={7} className="px-6 py-8 text-sm text-neutral-500">Cargando…</td></tr>
              )}
              {data?.breakdown_by_duration.map((b) => (
                <tr key={b.duration_days} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-6 py-3">
                    <div className="text-sm text-neutral-900">{formatDuration(b.duration_days)}</div>
                    <div className="text-xs text-neutral-500 tabular-nums">
                      {b.duration_days} días
                    </div>
                  </td>
                  <td className="px-6 py-3 tabular-nums text-sm text-neutral-900">{b.issued_count}</td>
                  <td className="px-6 py-3 tabular-nums text-sm text-neutral-900">
                    {formatMoney(b.issued_gross_cents, partner.currency)}
                  </td>
                  <td className="px-6 py-3 tabular-nums text-sm text-neutral-900">
                    {formatMoney(b.issued_ponos_net_cents, partner.currency)}
                  </td>
                  <td className="px-6 py-3 tabular-nums text-sm text-neutral-900">{b.redeemed_count}</td>
                  <td className="px-6 py-3 tabular-nums text-sm text-neutral-900">
                    {formatMoney(b.redeemed_gross_cents, partner.currency)}
                  </td>
                  <td className="px-6 py-3 tabular-nums text-sm text-neutral-900">
                    {formatMoney(b.redeemed_ponos_net_cents, partner.currency)}
                  </td>
                </tr>
              ))}
              {data && data.breakdown_by_duration.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-sm text-neutral-500">
                    Sin movimientos en el rango.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </DashCard>
    </div>
  );
}
